import { useCallback, useEffect, useRef, useState } from "react";
import { usePlaidLink, type PlaidLinkOnSuccess, type PlaidLinkOnExit } from "react-plaid-link";
import { Loader2, ShieldCheck, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { applicantApi, type ApiError } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { PlaidIDVConsentDialog } from "@/components/PlaidIDVConsentDialog";

/**
 * Plaid IDV (Identity Verification) drop-in. Used on:
 *   - Voucher flow step 1 (Identity verification)
 *   - Future: applicant flow step 2 (per PLAID_IDV_HANDOFF.md)
 *
 * IDV is different from the existing PlaidLinkButton (Income/Identity)
 * in two ways:
 *   - Different products in link_token_create (`identity_verification`
 *     + template_id, instead of `income_verification`).
 *   - No public_token exchange. onSuccess metadata includes the
 *     `identity_verification.id` (Plaid's idv_session_id); we POST that
 *     to /verify/plaid-idv/result and the backend fetches the full
 *     verification record from Plaid + writes signals.
 *
 * Lifecycle (matches PlaidLinkButton):
 *   1. Idle: dark CTA "Verify your identity".
 *   2. Click -> BIPA consent dialog -> POST /verify/plaid-idv/link-token.
 *      On 503 (IDV not configured for the env), we surface a toast +
 *      remain idle so the user can skip.
 *   3. Plaid Link opens via the PlaidLinkLauncher child (see below). User
 *      uploads gov ID + selfie.
 *   4. onSuccess -> POST /verify/plaid-idv/result with idv_session_id.
 *   5. Backend (steps-aware) writes identity.data_source_verified when the
 *      KYC/data-source path passed, or identity.gov_id_verified +
 *      identity.selfie_match when the document path ran; it returns
 *      data_source_passed / documentary_passed and we flip to "Verified" on
 *      EITHER path.
 *   6. Failure / cancel: per Juan's decision B (2026-06-08), we let the
 *      user retry up to 2 times. After max retries, surface a "we'll
 *      flag this for review" message and let the flow proceed.
 *
 * Why a child launcher instead of calling usePlaidLink here:
 *   react-plaid-link binds `open` to ONE Plaid handler created for the
 *   token it was initialized with. Once that handler has been exited /
 *   destroyed, feeding the same hook a new token and re-calling `open()`
 *   silently no-ops — the modal never reappears. Observed on UAT
 *   2026-06-13: first attempt opened fine, every retry "did nothing".
 *   The fix is to isolate usePlaidLink in PlaidLinkLauncher and remount
 *   it per link token (`key={linkToken}`), so every retry gets a fresh
 *   handler that auto-opens on mount. Unmounting on token=null runs
 *   react-plaid-link's cleanup and destroys the old handler.
 *
 * The retry counter is component-local (not persisted across page
 * reloads); a reload resets the counter — acceptable for the MVP. A
 * persisted skip (session.voucher_idv_skipped) is surfaced separately
 * via the `skipped` prop so a reopened, already-flagged session renders
 * the flagged state instead of a fresh CTA.
 */

/**
 * Default retry budget — the voucher flow's, unchanged since 2026-06-08.
 *
 * BF-C1 makes this a prop rather than a constant so the applicant flow can
 * pass 1 (ADR-012 R3.3: one retry, then the decline becomes the prominent
 * path). Defaulting to 2 keeps every existing call site — i.e. the voucher
 * flow — bit-for-bit identical, which BF-4/BF-5 require.
 */
const DEFAULT_MAX_RETRIES = 2;

/**
 * Module-level so the reference is stable across renders — it feeds the
 * useCallback dep arrays below, and a fresh object literal per render would
 * rebuild those callbacks every time.
 */
const DEFAULT_EXHAUSTED_TOAST = {
  title: "We'll flag this for review",
  description: "An operator will follow up; please continue with the rest of the flow.",
};

/**
 * Isolated usePlaidLink consumer. Mounted only while a link token exists
 * and remounted per token via `key` from the parent, so each launch gets
 * a fresh Plaid handler. Auto-opens once `ready` flips true. Renders
 * nothing — it's purely the Plaid Link driver.
 */
function PlaidLinkLauncher({
  linkToken,
  onSuccess,
  onExit,
}: {
  linkToken: string;
  onSuccess: PlaidLinkOnSuccess;
  onExit: PlaidLinkOnExit;
}) {
  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess, onExit });
  const openedRef = useRef(false);

  useEffect(() => {
    if (ready && !openedRef.current) {
      openedRef.current = true;
      open();
    }
  }, [ready, open]);

  return null;
}

interface PlaidIDVButtonProps {
  /** Voucher / applicant session token. */
  token: string | undefined;
  /** True once IDV succeeded in this flow (flips the button to a verified state). */
  verified?: boolean;
  /**
   * True when the session already recorded a skip (voucher_idv_skipped).
   * Renders the "flagged for operator review" state on a cold reopen so the
   * button matches the released Continue gate. The component-local retry
   * counter resets on reload, so without this a previously-skipped session
   * would invite a fresh attempt while Continue was already unblocked.
   */
  skipped?: boolean;
  /** Called once IDV succeeds; parent records the outcome. `data_source_passed`
   *  is the KYC/data-source path (no document needed); documentary/selfie are
   *  the document path. The gate releases on EITHER. */
  onSuccess?: (result: {
    documentary_passed: boolean;
    selfie_passed: boolean;
    data_source_passed: boolean;
  }) => void;
  /** Called when the user has exhausted retries and should move on (flagged-for-review state). */
  onMaxRetriesReached?: () => void;
  /**
   * How many attempts before the retry budget is exhausted. Defaults to the
   * voucher flow's 2; the applicant flow passes 1 (ADR-012 R3.3).
   */
  maxRetries?: number;
  /**
   * Render the exhausted state as the applicant's, not the voucher's.
   *
   * The voucher's exhausted copy says "Flagged for operator review", which is
   * true there — their Continue was gated and the skip is what releases it.
   * On the applicant side nothing is gated and declining is an offered choice
   * (ADR-012 R3), so that wording would announce a consequence that doesn't
   * exist and read as an accusation. The applicant surface supplies its own
   * copy via `exhaustedSlot` instead.
   */
  exhaustedSlot?: React.ReactNode;
  /**
   * Toast shown when the retry budget runs out. Defaults to the voucher's
   * "We'll flag this for review", which is accurate there and misleading on
   * the applicant side — nothing about their submission changes, so promising
   * an operator follow-up as a consequence of declining would be untrue.
   */
  exhaustedToast?: { title: string; description: string };
}

export function PlaidIDVButton({
  token,
  verified = false,
  skipped = false,
  onSuccess,
  onMaxRetriesReached,
  maxRetries = DEFAULT_MAX_RETRIES,
  exhaustedSlot,
  exhaustedToast = DEFAULT_EXHAUSTED_TOAST,
}: PlaidIDVButtonProps) {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loadingToken, setLoadingToken] = useState(false);
  const [submittingResult, setSubmittingResult] = useState(false);
  const [attempts, setAttempts] = useState(0);
  // BIPA-aware informed consent gate — see PlaidIDVConsentDialog.tsx +
  // PLAID_IDV_COMPLIANCE_AUDIT.md §7.2. The button click opens the
  // dialog; only after the user checks the consent box AND clicks
  // Continue do we call /verify/plaid-idv/link-token.
  const [consentOpen, setConsentOpen] = useState(false);

  // Shared "this attempt is over, count it" helper for the two failure
  // paths (Plaid Link exit + consent refusal). Functional update so we
  // never read a stale `attempts`.
  const recordFailedAttempt = useCallback(
    (cancelledCopy: { title: string }) => {
      setAttempts((prev) => {
        const next = prev + 1;
        if (next >= maxRetries) {
          toast(exhaustedToast);
          onMaxRetriesReached?.();
        } else {
          toast({
            title: cancelledCopy.title,
            description: `${maxRetries - next} attempt${maxRetries - next === 1 ? "" : "s"} left before we flag this for review.`,
          });
        }
        return next;
      });
    },
    [onMaxRetriesReached, maxRetries, exhaustedToast],
  );

  const handleSuccess = useCallback<PlaidLinkOnSuccess>(async (_publicToken, _metadata) => {
    if (!token) return;
    // NOTE: Plaid Link's onSuccess metadata does NOT include the
    // identity_verification id (it only carries institution/accounts/
    // link_session_id). Earlier code read metadata.identity_verification.id,
    // which was always undefined, so /result was never called and IDV never
    // recorded (this stranded a real voucher — see plaid-data-source-idv.md).
    // We now post just the session token; the backend resolves the user's
    // latest IDV session by client_user_id.
    setSubmittingResult(true);
    try {
      const result = await applicantApi.plaidIdvResult(token);
      const docPassed = result.documentary_status === "success";
      const selfiePassed = result.selfie_status === "success";
      // Plaid's prod template runs a data-source (KYC) check first and skips
      // document+selfie when it passes — so a successful IDV often has
      // data_source_passed=true with doc/selfie null. Treat EITHER complete
      // path as verified (was a bug: only doc+selfie counted, so every
      // data-source pass fell into the "failed" branch and got flagged).
      const dataSourcePassed = result.data_source_passed === true;

      if (dataSourcePassed || (docPassed && selfiePassed)) {
        toast({ title: "Identity verified", description: "Thanks — we confirmed your identity." });
        onSuccess?.({
          documentary_passed: docPassed,
          selfie_passed: selfiePassed,
          data_source_passed: dataSourcePassed,
        });
      } else {
        // Partial / failed. Bump attempts; if exhausted, hand off.
        setAttempts((prev) => {
          const next = prev + 1;
          if (next >= maxRetries) {
            toast(exhaustedToast);
            onMaxRetriesReached?.();
          } else {
            toast({
              title: "Verification didn't match",
              description: `Please try again (${maxRetries - next} attempt${maxRetries - next === 1 ? "" : "s"} left).`,
              variant: "destructive",
            });
          }
          return next;
        });
      }
    } catch (err) {
      const apiErr = err as ApiError;
      toast({
        title: "Couldn't record verification",
        description: apiErr.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmittingResult(false);
      // Unmount the launcher (destroys the consumed Plaid handler).
      setLinkToken(null);
    }
  }, [token, onSuccess, onMaxRetriesReached, maxRetries, exhaustedToast]);

  const handleExit = useCallback<PlaidLinkOnExit>(() => {
    // User cancelled the Plaid modal — counts toward the retry budget
    // (per Juan's 2026-06-13 decision: any exit, whether failure or
    // user-cancellation, is one of the 2 chances). Pre-2026-06-13 only
    // an actual failed verification counted, which made the gate easy
    // to never-trip on by hitting Cancel forever. Clearing linkToken
    // unmounts the launcher so the next attempt mounts a fresh handler.
    setLinkToken(null);
    recordFailedAttempt({ title: "Verification cancelled" });
  }, [recordFailedAttempt]);

  // Button click opens the BIPA consent dialog. The actual /link-token
  // call only fires from `handleConsent` after the user checks the
  // consent box + clicks Continue.
  const handleClick = () => {
    if (!token) return;
    if (verified) return;
    setConsentOpen(true);
  };

  // "I'd rather not" on the consent dialog also counts toward the
  // retry budget. Same threshold as IDV failures + cancellations.
  // After the retry budget, fires onMaxRetriesReached which calls the
  // backend skip endpoint and unblocks the Continue button.
  const handleRefuse = () => {
    recordFailedAttempt({ title: "OK, you can try again later" });
  };

  const handleConsent = async () => {
    if (!token) return;
    setLoadingToken(true);
    try {
      const result = await applicantApi.plaidIdvLinkToken(token);
      // Setting linkToken mounts a fresh PlaidLinkLauncher (keyed by the
      // token) which auto-opens Plaid Link. Close the consent dialog now;
      // Plaid's modal takes over the screen.
      setLinkToken(result.link_token);
      setConsentOpen(false);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 503) {
        toast({
          title: "Identity verification not available",
          description: "This environment isn't configured for ID verification — feel free to skip.",
        });
      } else {
        toast({
          title: "Couldn't start identity verification",
          description: apiErr.message ?? "Please try again or skip.",
          variant: "destructive",
        });
      }
      // Leave the dialog open on error so the user can retry without
      // having to re-tick the consent box.
    } finally {
      setLoadingToken(false);
    }
  };

  if (verified) {
    return (
      <Button
        type="button"
        disabled
        className="w-full bg-emerald-600 hover:bg-emerald-600 text-white cursor-default"
      >
        <CheckCircle2 className="w-4 h-4 mr-2" />
        Identity verified
      </Button>
    );
  }

  // `skipped` (persisted) OR a within-session exhaustion both land on the
  // flagged-for-review state so the UI is consistent across reloads.
  const exhausted = skipped || attempts >= maxRetries;
  if (exhausted && exhaustedSlot !== undefined) return <>{exhaustedSlot}</>;
  if (exhausted) {
    return (
      <div className="space-y-2">
        <Button type="button" disabled className="w-full" variant="outline">
          <AlertCircle className="w-4 h-4 mr-2 text-amber-500" />
          Flagged for operator review
        </Button>
        <p className="text-xs text-slate-500 text-center">
          An operator will follow up. You can continue with the rest of the flow.
        </p>
      </div>
    );
  }

  return (
    <>
      <Button
        type="button"
        onClick={handleClick}
        disabled={loadingToken || submittingResult}
        className="w-full bg-slate-900 hover:bg-slate-800 text-white"
      >
        {submittingResult ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <ShieldCheck className="w-4 h-4 mr-2" />
        )}
        {submittingResult
          ? "Recording verification…"
          : attempts > 0
            ? `Try identity verification again (${maxRetries - attempts} left)`
            : "Verify your identity"}
      </Button>

      {/* Remounted per token so each retry gets a fresh Plaid handler. */}
      {linkToken && (
        <PlaidLinkLauncher
          key={linkToken}
          linkToken={linkToken}
          onSuccess={handleSuccess}
          onExit={handleExit}
        />
      )}

      <PlaidIDVConsentDialog
        open={consentOpen}
        onOpenChange={setConsentOpen}
        onConsent={handleConsent}
        onRefuse={handleRefuse}
        starting={loadingToken}
      />
    </>
  );
}
